import { format } from "date-fns";
import { Tooltip } from "react-tooltip";

import { SubscriptionData, TeamUsage } from "~/api/types";
import { Icon } from "~/assets/icons/Icon";
import {
  getPrice,
  getPriceWithoutCurrency,
  getTotalPrice,
} from "~/helpers/helpers";

import ProgressBar from "../ui/progressBar/ProgressBar";
import styles from "./Subscription.module.scss";

const paymentMethodsIcons: { [key: string]: JSX.Element } = {
  visa: <Icon iconName="VisaCardIcon" width="35px" height="24px" />,
  mastercard: <Icon iconName="MasterCardIcon" width="35px" height="24px" />,
};

export const getTooltipContent = (
  price: string,
  unit: string,
  name: string,
) => {
  return `${price} per ${unit} ${name}`;
};

const priceWithAddons = (
  basePrice: number,
  addons: number[],
  currency: string,
): string => {
  const totalPrice = basePrice + addons?.reduce((acc, addon) => acc + addon, 0);

  return getPrice(totalPrice, currency);
};

export const calculatePriceFromSubscription = (
  currency: string,
  basePrice: number,
  addons: { quantity: number; price: { amount: number; divide_by: number } }[],
): string => {
  const addonPrices = addons?.map(
    (addon) =>
      (addon?.quantity * addon?.price?.amount) / addon?.price?.divide_by,
  );

  return priceWithAddons(basePrice, addonPrices, currency);
};

export const getPlanUsageComponentData = (
  planUsage: TeamUsage,
  subscription: SubscriptionData,
) => {
  const totalPrice = calculatePriceFromSubscription(
    subscription?.price?.currency,
    subscription?.price?.amount,
    subscription?.subscription_addons
      ?.filter((addon) => !addon?.price?.metered)
      .map((addon) => ({
        ...addon,
        price: {
          ...addon?.price,
          divide_by: Number(addon?.price?.divide_by),
        },
      })),
  );

  const validUntil = subscription?.cancel_at_period_end
    ? `Valid until ${format(subscription?.cancel_at || subscription?.period_end_at, "MMMM dd',' yyyy")}`
    : subscription?.period_end_at
      ? ["active", "past_due"].includes(subscription.status)
        ? `Renews on ${format(subscription?.period_end_at, "MMMM dd',' yyyy")}`
        : ["trialing"].includes(subscription?.status)
          ? `First payment on ${format(subscription?.period_end_at, "MMMM dd',' yyyy")}`
          : ""
      : "";

  let statusTitle: string;

  switch (subscription?.status) {
    case "intent":
    case "created":
    case "incomplete":
      statusTitle = "Incomplete";
      break;
    case "trialing":
      statusTitle = "On Trial";
      break;
    case "past_due":
      statusTitle = "Payment Overdue";
      break;
    default:
      statusTitle = subscription?.status ?? "";
      break;
  }

  return {
    currentPlan: subscription?.price?.product?.name ?? "",
    price: {
      value: totalPrice,
      interval: subscription?.price?.recurring?.interval ?? "",
    },
    status: subscription?.status ?? "",
    statusTitle,
    validUntil,
    // team_limits come back as null for unlimited caps (usage-billed
    // plans) — keep the null so ProgressBar renders "no limit".
    progressData: [
      {
        title: "Projects",
        min: planUsage?.current_usage?.site_max ?? 0,
        max: planUsage?.team_limits?.site_max ?? null,
      },
      {
        title: "Teammates",
        min: planUsage?.current_usage?.membership_max ?? 0,
        max: planUsage?.team_limits?.membership_max ?? null,
      },
      {
        title: "Keywords",
        min: planUsage?.current_usage?.keyword_max ?? 0,
        max: planUsage?.team_limits?.keyword_max ?? null,
      },
      {
        title: "Page Optimization",
        min: planUsage?.current_usage?.page_max ?? 0,
        max: planUsage?.team_limits?.page_max ?? null,
      },
    ],
    payment_method: subscription?.payment_method ?? null,
  };
};

export const getPaymentMethodsTableComponentData = (
  paymentMethods: SubscriptionData["payment_method"],
) => {
  return [
    {
      "Card type": (
        <div className={styles.cardType}>
          {paymentMethodsIcons?.[paymentMethods?.data?.brand]}
          <span className={styles.capitalizeTitle}>
            {paymentMethods?.data?.display_brand}{" "}
          </span>
          ending in {paymentMethods?.data?.last4}
        </div>
      ),
      Status: <span className={styles.greenBage}>Primary</span>,
      "Expiry date": (
        <span>{`${paymentMethods?.data?.exp_month}/${paymentMethods?.data?.exp_year}`}</span>
      ),
    },
  ];
};

export const getAddondTableComponentData = (
  subscription: SubscriptionData,
  planUsage: TeamUsage,
) => {
  const subscriptionAddons = subscription?.subscription_addons;

  const addons = subscriptionAddons
    ?.filter((addon) => !addon?.price?.metered)
    ?.map((addon) => {
      const productKey = addon?.price?.product?.key as
        | "site_max"
        | "membership_max"
        | "keyword_max"
        | "page_max";

      // null plan limit = unlimited cap (usage-billed plans): the add-on
      // quantity tracks usage rather than extending a cap, so show the
      // plain count instead of "used of quantity".
      const planLimit = planUsage?.plan_limits?.[productKey] ?? null;

      const getUsedData = () => {
        return planUsage?.current_usage?.[productKey] -
          planUsage?.plan_limits?.[productKey] <
          0
          ? 0
          : planUsage?.current_usage?.[productKey] -
              planUsage?.plan_limits?.[productKey];
      };

      const addonPrice = getPrice(addon.price.amount, addon.price.currency);
      const addonName = addon.price.product.name;

      // Stripe calc logic
      const packs = Math.ceil(addon.quantity / Number(addon.price.divide_by));
      const calculatedPrice = packs * addon.price.amount;
      const totalPrice = getPrice(calculatedPrice, addon.price.currency);

      return {
        Feature: addonName,
        "Usage Limits": (
          <div className={styles.progressBarWrapper}>
            {!addon.price.product.singleton &&
              (planLimit === null ? (
                <ProgressBar
                  min={planUsage?.current_usage?.[productKey] ?? 0}
                  max={null}
                />
              ) : (
                <ProgressBar min={getUsedData()} max={addon.quantity} />
              ))}
          </div>
        ),
        "Price per Package": (
          <div className={styles.addonPriceIconWrapper}>
            {addonPrice}
            <a data-tooltip-id={addonName}>
              <Icon iconName="InfoIcon" width="1rem" height="1rem" />
            </a>
            <Tooltip
              style={{ borderRadius: "var(--border-radius)" }}
              id={addonName}
              content={getTooltipContent(
                getPrice(addon.price.amount, addon.price.currency),
                addon?.price?.divide_by,
                addonName,
              )}
              place="top"
            />
          </div>
        ),
        "Total Price": (
          <div className={styles.addonPriceIconWrapper}>{totalPrice}</div>
        ),
      };
    });

  return addons;
};
export const getPayAsYouGoComponentDataFreeLimits = (
  subscriptionAddons: SubscriptionData["subscription_addons"],
  planUsage: TeamUsage,
) => {
  return subscriptionAddons
    .filter((addon) => addon.price.metered && addon.price.tiers)
    .map((addon) => {
      const productKey = addon.price.product
        .key as keyof typeof planUsage.free_limits;
      const freeLimit = planUsage?.free_limits?.[productKey];
      const usedFreeLimit =
        planUsage?.current_usage?.[
          addon.price.product.key as keyof typeof planUsage.current_usage
        ];
      const notFreePrice =
        addon.price.tiers?.find(
          (tier) => Number(tier.unit_amount_decimal) !== 0,
        )?.unit_amount_decimal || 0;
      const priceWithoutCurrency = getPriceWithoutCurrency(
        Number(notFreePrice),
      );
      const total =
        usedFreeLimit > freeLimit
          ? priceWithoutCurrency * Math.abs(freeLimit - usedFreeLimit)
          : 0;

      return {
        Feature: addon.price.product.name,
        "Free Limits": (
          <div className={styles.progressBarWrapper}>
            <ProgressBar min={usedFreeLimit} max={freeLimit} />
          </div>
        ),
        "Price Per Unit": getPrice(Number(notFreePrice), addon.price.currency),
        "Total Price": getTotalPrice(total),
      };
    });
};

export const getPayAsYouGoComponentDataNoFreeLimits = (
  subscriptionAddons: SubscriptionData["subscription_addons"],
) => {
  return subscriptionAddons
    ?.filter((addon) => !addon.price.tiers && addon.price.metered)
    .map((addon) => {
      return {
        Feature: addon.price.product.name,
        QTY: <div>{addon.quantity}</div>,
        "Price Per Unit": getPrice(addon.price.amount, addon.price.currency),
      };
    });
};

const SUBSCRIPTION_PENDING_STATUSES = ["intent", "created", "incomplete"];
export const isSubscriptionStatusPending = (status: string) =>
  SUBSCRIPTION_PENDING_STATUSES.includes(status);

// Statuses a subscription lands on after a successful checkout. Everything
// else that is non-pending (past_due, unpaid, canceled, incomplete_expired)
// means the activation went wrong.
const SUBSCRIPTION_ACTIVATED_STATUSES = ["trialing", "active"];
export const isSubscriptionStatusActivated = (status: string) =>
  SUBSCRIPTION_ACTIVATED_STATUSES.includes(status);
